import { useReactiveVar } from '@apollo/client';
import envConfig from '@hulu/env-config';
import { useLocation } from '@hulu/react-router-dom';
import { Auth } from 'aws-amplify';
import { initialize, LDClient, LDContext } from 'launchdarkly-js-client-sdk';
import debounce from 'lodash/debounce';
import React, { createContext, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { selectedOrganizationIdVar, userAmsUserIdVar } from '../../constants';
import { ANONYMOUS_LD_CONTEXT, buildLaunchDarklyContext, LaunchDarklyUserInfo } from './buildLaunchDarklyContext';
import { resolveLaunchDarklySession } from './resolveLaunchDarklySession';

export interface LaunchDarklyContextManager {
  client: LDClient | undefined;
  currentContext: LDContext | undefined;
  isReady: boolean;
}

const defaultContext: LaunchDarklyContextManager = {
  client: undefined,
  currentContext: undefined,
  isReady: false,
};

export const LaunchDarklyContext = createContext<LaunchDarklyContextManager>(defaultContext);

interface LaunchDarklyProviderProps {
  children: ReactNode;
}

export const LaunchDarklyProvider: React.FC<LaunchDarklyProviderProps> = ({ children }) => {
  const [client, setClient] = useState<LDClient | null>(null);
  const [currentContext, setCurrentContext] = useState<LDContext | undefined>();
  const [isReady, setIsReady] = useState(false);
  const [sessionUserInfo, setSessionUserInfo] = useState<LaunchDarklyUserInfo | null>(null);
  const [contextManager, setContextManager] = useState<LaunchDarklyContextManager>(defaultContext);

  const userAmsUserId = useReactiveVar(userAmsUserIdVar);
  const selectedOrganizationId = useReactiveVar(selectedOrganizationIdVar);
  const location = useLocation();

  const identifyCallCount = useRef(0);
  const identifyResetTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const pendingContextRef = useRef<LDContext | null>(null);

  useEffect(() => {
    const initializeLaunchDarkly = async (): Promise<void> => {
      try {
        const clientSideId = envConfig.REACT_APP_LAUNCH_DARKLY_CLIENT_SIDE_ID;

        if (!clientSideId) {
          console.error('LaunchDarkly client side ID not found in envConfig');
          return;
        }

        const ldClient = initialize(clientSideId, ANONYMOUS_LD_CONTEXT);
        setClient(ldClient);

        await ldClient.waitForInitialization(30);

        setCurrentContext(ANONYMOUS_LD_CONTEXT);
        setIsReady(true);
      } catch (error) {
        console.error('Failed to initialize LaunchDarkly:', error);
      }
    };

    void initializeLaunchDarkly();
  }, []);

  useEffect(() => {
    if (!userAmsUserId) {
      setSessionUserInfo(null);
      return;
    }

    let cancelled = false;

    Auth.currentSession()
      .then((session) => {
        if (!cancelled && session.isValid()) {
          setSessionUserInfo(session.getIdToken().decodePayload() as LaunchDarklyUserInfo);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSessionUserInfo(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [userAmsUserId]);

  const { userInfo: resolvedUserInfo, organizationId: resolvedOrganizationId } = useMemo(
    () =>
      resolveLaunchDarklySession({
        pathname: location.pathname,
        userAmsUserId,
        marketerSessionUserInfo: sessionUserInfo,
        selectedOrganizationId,
      }),
    [location.pathname, selectedOrganizationId, sessionUserInfo, userAmsUserId]
  );

  const performIdentify = useCallback(
    async (nextContext: LDContext): Promise<void> => {
      if (!client) return;

      if (identifyResetTimer.current) {
        clearTimeout(identifyResetTimer.current);
      }

      identifyResetTimer.current = setTimeout(() => {
        identifyCallCount.current = 0;
      }, 60000);

      identifyCallCount.current += 1;

      if (identifyCallCount.current > 50) {
        console.warn('[LaunchDarkly] High identify() call rate detected', {
          count: identifyCallCount.current,
          context: nextContext,
        });
      }

      try {
        await client.identify(nextContext);
        setCurrentContext(nextContext);
      } catch (error) {
        console.error('Failed to identify LaunchDarkly context:', error);
      }
    },
    [client]
  );

  const debouncedIdentify = useMemo(() => debounce(performIdentify, 300), [performIdentify]);

  useEffect(() => {
    if (!client || !isReady) {
      return;
    }

    const nextContext = buildLaunchDarklyContext({
      userInfo: resolvedUserInfo,
      organizationId: resolvedOrganizationId,
    });

    pendingContextRef.current = nextContext;
    void debouncedIdentify(nextContext);

    return () => {
      debouncedIdentify.cancel();
    };
  }, [client, isReady, resolvedUserInfo, resolvedOrganizationId, debouncedIdentify]);

  useEffect(() => {
    if (client) {
      setContextManager({
        client,
        currentContext,
        isReady,
      });
    }
  }, [client, currentContext, isReady]);

  return <LaunchDarklyContext.Provider value={contextManager}>{children}</LaunchDarklyContext.Provider>;
};
